/**
 * buildLlmPrompt.ts — System- + User-Prompt-Konstruktion für Mistral.
 *
 * Pure Function, deterministische Outputs. Jeder AssessmentTexts-Schlüssel
 * bekommt sein eigenes Template — Mistral soll exakt das angeforderte Format
 * liefern, ohne Markdown, ohne Einleitung, ohne Erklärung.
 *
 * WICHTIG:
 *   - heuristicsContext MUSS bereits pseudonymisiert sein (Aufrufer-Pflicht).
 *   - Diese Funktion fügt KEIN PII hinzu — Score-Werte und Branchen sind
 *     fachlich neutral, brauchen keine Maskierung.
 */

import type { ScoreResult, TrafficLight } from '@tragfaehigkeit/core';

/** Subset der AssessmentTexts-Felder, die das LLM befüllen kann. */
export type LlmFieldKey =
  | 'verdictLongParagraph'
  | 'verdictReasoning'
  | 'recommendationDetailed'
  | 'constraintDiscussion'
  | 'riskIndicators'
  | 'strengthIndicators'
  | 'revenueAssessment'
  | 'costsAssessment'
  | 'profitAssessment'
  | 'hourlyRateAssessment'
  | 'liquidityAssessment'
  | 'runwayAssessment'
  | 'utilizationAssessment'
  | 'gapInterpretation'
  | 'conditionsParagraph'
  | 'reevaluationProposal';

export interface BuildLlmPromptInput {
  /**
   * Pseudonymisierter Zusammenfassungs-Text mit den fachlich relevanten
   * Strukturdaten — typischerweise ein Konkatenat aus Heuristics-Output,
   * Financial-Kennzahlen und Dimension-Free-Text. Aufrufer-Pflicht: durch
   * pseudonymize() geschickt.
   */
  readonly heuristicsContext: string;
  readonly scoreResult: ScoreResult;
  readonly fieldKey: LlmFieldKey;
}

export interface LlmPromptOutput {
  readonly systemPrompt: string;
  readonly userPrompt: string;
}

const TRAFFIC_DE: Readonly<Record<TrafficLight, string>> = {
  green: 'grün',
  yellow: 'gelb',
  red: 'rot',
};

const SYSTEM_PROMPT = `Du bist eine datenschutz-bewusste fachkundige Assistenz für die Erstellung von
Stellungnahmen zur Tragfähigkeit selbständiger Tätigkeiten nach § 10 Abs. 2 Nr. 5
SGB II in Deutschland (Bürgergeld).

Sprache:
- Sie-Form, behördentauglich, präzise.
- Orientiere dich an den Fachlichen Weisungen der Bundesagentur für Arbeit zu § 10 SGB II.
- Keine Marketing-Floskeln, keine Coaching-Sprache, keine Emojis, kein "ganzheitlich",
  "innovativ", "Mehrwert", "transformieren".
- Konkrete Zahlen statt Adjektiven, wo möglich.

Datenschutz:
- Die Eingabedaten sind PSEUDONYMISIERT. Schreibe immer "die Mandant:in"
  statt eines Namens. Verwende keine Klarnamen, Adressen, Aktenzeichen oder
  anderen personenbezogenen Daten — die werden später vom Berater eingefügt.
- Wenn der Input bereits "die Mandant:in", "[Großstadt]", "[Jobcenter]" oder
  ähnliche Platzhalter enthält, übernimm sie unverändert.

Output-Format:
- Liefere NUR den angeforderten Text. Keine Einleitung wie "Hier ist der Vorschlag",
  keine Erklärung was du gemacht hast, keine Markdown-Formatierung (kein **bold**,
  keine #-Headlines, keine - Bulletpoints, außer ausdrücklich angefordert).
- Wenn die Datenbasis für eine fundierte Aussage nicht reicht, beginne mit
  "Vorschlag nicht möglich — " und nenne den fehlenden Datenpunkt.`;

/**
 * Pro-Feld-Templates. Jedes Template steuert Tonalität, Länge und Struktur
 * für das jeweilige AssessmentTexts-Feld.
 */
const FIELD_INSTRUCTIONS: Readonly<Record<LlmFieldKey, string>> = {
  verdictLongParagraph: `Verfasse zwei Absätze (zusammen 150–250 Wörter) zur Gesamtbeurteilung der
Tragfähigkeit. Erster Absatz: das Verdict (Tragfähigkeit gegeben /
eingeschränkt gegeben / aktuell nicht gegeben) und der Score-Wert {{SCORE}}/100
mit Ampel {{TRAFFIC}}. Zweiter Absatz: die zwei wichtigsten tragenden Gründe
aus den Daten (Zahlen nennen, wenn vorhanden).`,

  verdictReasoning: `Verfasse 80–150 Wörter zur Begründung des Verdicts. Knapp und sachlich:
welche Datenpunkte stützen die Einschätzung der Tragfähigkeit auf Ampel
{{TRAFFIC}} (Score {{SCORE}}/100)? Keine Bullet-Liste — Fließtext.`,

  recommendationDetailed: `Verfasse eine konkrete, umsetzbare Empfehlung in 100–180 Wörtern. Sie
beginnt mit der Kernaussage (Fortführung / konditionierte Fortführung /
Vermittlungsbemühungen in abhängige Beschäftigung) und nennt 2–3 konkrete
Schritte oder Auflagen. Sie-Form, kein Coaching-Stil.`,

  constraintDiscussion: `Diskutiere in 80–140 Wörtern die Rahmenbedingungen und Einschränkungen,
die für die Bewertung relevant sind (z.B. Branche, Geschäftsalter,
private Konstellation, Datenqualität). Sachlich, kein Werturteil.`,

  riskIndicators: `Liste die Risiko-Indikatoren als kurze Bullet-Liste (3–7 Stichpunkte,
jeweils ein Satz). Format: "- Indikator: kurze Erläuterung mit Zahl, falls
vorhanden." Nur tatsächliche Risiken aus den Daten — keine Spekulation.`,

  strengthIndicators: `Liste die Stärke-Indikatoren als kurze Bullet-Liste (2–5 Stichpunkte,
jeweils ein Satz). Format wie bei Risiken. Wenn keine klaren Stärken
erkennbar: "Keine ausgeprägten Stärke-Indikatoren in der Datenbasis."`,

  revenueAssessment: `Bewerte den Umsatz in 60–100 Wörtern: durchschnittlicher Monatsumsatz,
Schwankungsbreite, Trend. Konkrete Zahlen aus dem Input verwenden.`,

  costsAssessment: `Bewerte die Kostenstruktur in 60–100 Wörtern: fixe vs. variable Kosten,
Verhältnis zum Umsatz, ungewöhnliche Posten. Konkrete Zahlen.`,

  profitAssessment: `Bewerte den Gewinn in 60–100 Wörtern: Gewinnniveau absolut, Marge,
Tragfähigkeit für Lebensunterhalt. Konkrete Zahlen.`,

  hourlyRateAssessment: `Bewerte den Stundensatz in 60–100 Wörtern: errechneter Netto-Stundensatz,
Vergleich zum branchenüblichen Mindeststundensatz, Auslastung.`,

  liquidityAssessment: `Bewerte die Liquidität in 50–80 Wörtern: aktueller Bestand, Verhältnis
zu monatlichem Bedarf, Risiko eines Zahlungsengpasses.`,

  runwayAssessment: `Bewerte die Runway in 50–80 Wörtern: wie lange reicht die Liquidität bei
aktueller Burn-Rate? Konkrete Wochen-Angabe.`,

  utilizationAssessment: `Bewerte die Auslastung in 50–80 Wörtern: aktuelle Auslastung in %,
Potenzial nach oben, Risiko nach unten.`,

  gapInterpretation: `Interpretiere die Tragfähigkeitslücke in 80–120 Wörtern: Differenz zwischen
BG-Bedarf und gedecktem Anteil. Wenn keine Lücke vorhanden: das benennen.
Konkrete Eurobeträge.`,

  conditionsParagraph: `Verfasse Auflagen für eine konditionierte Fortführung in 80–140 Wörtern.
Konkret und überprüfbar: BWA-Vorlage-Rhythmus, Mindest-Stundensatz,
Auslastungs-Mindestmarke, Re-Evaluierungs-Datum.`,

  reevaluationProposal: `Schlage einen Re-Evaluierungs-Termin und -Inhalt in 60–100 Wörtern vor.
Konkretes Datum (üblich 6 Monate), was bis dahin nachgewiesen werden soll.`,
};

/**
 * Baut System- + User-Prompt für den angeforderten AssessmentTexts-Schlüssel.
 *
 * @throws Error wenn fieldKey unbekannt ist (zur Compile- und Runtime-Sicherheit).
 */
export function buildLlmPrompt(input: BuildLlmPromptInput): LlmPromptOutput {
  const instr = FIELD_INSTRUCTIONS[input.fieldKey];
  if (!instr) {
    throw new Error(`buildLlmPrompt: unbekannter fieldKey "${input.fieldKey}"`);
  }

  const traffic = TRAFFIC_DE[input.scoreResult.trafficLight];
  const scoreStr = String(input.scoreResult.totalScore);
  const filled = instr.replace(/\{\{SCORE\}\}/g, scoreStr).replace(/\{\{TRAFFIC\}\}/g, traffic);

  const userPrompt =
    `Aufgabe für das Feld "${input.fieldKey}":\n${filled}\n\n` +
    `Gesamtscore: ${scoreStr}/100 (Ampel: ${traffic}, Konfidenz: ${String(input.scoreResult.totalConfidencePercent)}%).\n\n` +
    `--- Pseudonymisierter Datenkontext ---\n${input.heuristicsContext.trim()}\n--- Ende ---\n\n` +
    `Liefere nur den angeforderten Text — keine Einleitung, kein Markdown.`;

  return {
    systemPrompt: SYSTEM_PROMPT,
    userPrompt,
  };
}

/** Test-Helper: Liste aller unterstützten Feld-Keys (für Coverage-Loops). */
export const LLM_FIELD_KEYS: ReadonlyArray<LlmFieldKey> = Object.keys(
  FIELD_INSTRUCTIONS,
) as ReadonlyArray<LlmFieldKey>;
